import {
  Component,
  ChangeDetectorRef,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  QaiTableComponent,
  TableRow,
  QaiLoadingComponent,
  QaiBadgeKind,
  KebabMenuItem,
  BadgeCell,
  SortDirection,
} from '@qai/ui';
import { finalize, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import {
  ISimulation,
  TableRowSimulationNameObject,
} from 'src/app/interfaces/self-serve/responses/isimulation';
import {
  CallTypeLabelMap,
  SimulationConstants,
} from 'src/app/utils/constants/self-serve/simulation.constants';
import { AppConstants } from 'src/app/app-constants';
import { formatLongMonthDate } from 'src/app/utils/date-parser.util';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/iassignment';
import { PaginationControllerService } from 'src/app/services/self-serve/pagination-controller.service';

const SIMULATION_TABLE_HEADERS = [
  'Name',
  'Product',
  'Date last modified',
  'Status',
  'Simulation type',
];

const EXTENDED_SIMULATION_TABLE_HEADERS = [
  'Name',
  'Product',
  'Date last modified',
  'Status',
  'Used in',
  'Simulation type',
];

@Component({
  selector: 'all-simulations-table',
  standalone: true,
  templateUrl: './all-simulations-table.component.html',
  styleUrls: ['./all-simulations-table.component.scss'],
  imports: [CommonModule, QaiTableComponent, QaiLoadingComponent],
})
export class AllSimulationsTableComponent implements OnInit {
  tableData: TableRow[] = [];
  headers: string[] = [];
  isLoading = true;
  initialSortColumn: string = EXTENDED_SIMULATION_TABLE_HEADERS[2] || SIMULATION_TABLE_HEADERS[2];
  initialSortDirection: SortDirection = 'desc';
  options: { label: string; value: string }[] = [];
  isTooltipLoading = false;
  private simulations: ISimulation[] = [];
  @ViewChild(QaiTableComponent)
  qaiTableRef: QaiTableComponent | null = null;

  @Input() selectedAccount: { label: string; value: string } | undefined;
  @Input() selectedAccountUuid: string = '';
  @Input() refreshKey: number = 0;
  @Input() selectedAccountHasAuthor: boolean = false;
  @Input() selectedAccountHasAssignmentManager: boolean = false;

  @Output() deleteSimulation = new EventEmitter<{
    id: string | number;
    rowData: TableRow;
    moduleUuid?: string;
  }>();
  @Output() duplicateSimulation = new EventEmitter<{
    id: string;
    name: string;
    moduleUuid?: string;
  }>();

  currentPage: number = 1;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private assignmentsService: AssignmentsService,
    private cdRef: ChangeDetectorRef,
    private PaginationControllerService: PaginationControllerService,
    private clipboard: Clipboard
  ) { }

  ngOnInit(): void {
    this.updateHeaders();
    this.initializePagination();
  }

  private initializePagination(): void {
    this.currentPage = this.PaginationControllerService.getSimulationsCurrentPage();
  }

  private updateHeaders(): void {
    this.headers =
      this.selectedAccountHasAuthor && this.selectedAccountHasAssignmentManager
        ? EXTENDED_SIMULATION_TABLE_HEADERS
        : SIMULATION_TABLE_HEADERS;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAccount']) {
      this.loadSimulations();
    }
    if (changes['refreshKey']) {
      this.loadSimulations();
    }
    if (
      changes['selectedAccountHasAuthor'] ||
      changes['selectedAccountHasAssignmentManager']
    ) {
      this.updateHeaders();
    }
  }

  loadSimulations() {
    const accountUuid = this.selectedAccount?.value || this.selectedAccountUuid;

    if (!accountUuid) {
      console.error('No valid account UUID found to load simulations.');
      this.isLoading = false;
      return;
    }

    this.authorService
      .findAllSimulations(accountUuid)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: (simulations) => {
          this.simulations = simulations;
          this.tableData = this.transformApiDataToTableRows(simulations);
        },
        error: (err: any) => {
          console.error('Error loading simulations:', err);
          this.isLoading = false;
        },
      });
  }

  private transformApiDataToTableRows(apiData: ISimulation[]): TableRow[] {
    return apiData
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .map((simulation) => {
        const row = this.createSimulationTableRow(
          simulation.uuid,
          simulation.name,
          simulation.moduleName,
          formatLongMonthDate(simulation.updatedAt),
          this.getStatusBadge(simulation.status),
          this.formatAssignmentCount(simulation.assignmentCount, simulation.status),
          this.getCallTypeLabel(simulation.callType),
          simulation.callType?.toUpperCase() === AppConstants.CALL_TYPE_GUIDED
            ? []
            : this.buildKebabMenuItems(simulation),
          simulation.moduleUuid
        );

        // Add moduleUuid to the row for direct access
        (row as any).moduleUuid = simulation.moduleUuid;
        // Store original UUID separately for easy extraction
        (row as any).simulationUuid = simulation.uuid;

        return row;
      });
  }

  private getCallTypeLabel(callType?: string): string {
    if (!callType) return 'Unknown';

    const key = callType.toLowerCase() as keyof typeof CallTypeLabelMap;
    return CallTypeLabelMap[key] || this.capitalizeFirstLetter(callType);
  }

  private capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private buildKebabMenuItems(simulation: ISimulation): KebabMenuItem[] {
    const items: KebabMenuItem[] = [{ type: 'click', text: 'Duplicate', }];

    if (simulation.status?.toLowerCase() !== 'published') {
      items.unshift({ type: 'click', text: 'Delete' });
    }

    items.push({ type: 'click', text: 'Copy UUID', description: simulation.uuid });

    return items;
  }

  private getStatusBadge(status: string | null): {
    kind: QaiBadgeKind;
    label: string;
  } {
    if (!status) {
      return { kind: 'yellow', label: SimulationConstants.DRAFT };
    }

    switch (status.toLowerCase()) {
      case 'draft':
        return { kind: 'yellow', label: SimulationConstants.DRAFT };
      case 'published':
        return { kind: 'mist-blue', label: SimulationConstants.PUBLISHED };
      case 'testing':
        return { kind: 'gray-blue', label: SimulationConstants.TESTING };
      default:
        return { kind: 'yellow', label: status };
    }
  }

  createSimulationTableRow(
    id: string | number,
    name: string,
    moduleName: string,
    date: string,
    status: { kind: QaiBadgeKind; label: string },
    assignmentCount: string,
    simulationType: string,
    options: KebabMenuItem[],
    moduleUuid: string = ''
  ): TableRow {
    // Create unique ID by combining UUID and moduleUuid
    const uniqueId = moduleUuid ? `${id}_${moduleUuid}` : String(id);

    const row: any = {
      id: uniqueId,
      [this.headers[0]]: { type: 'text', value: name },
      [this.headers[1]]: { type: 'text', value: moduleName },
      [this.headers[2]]: { type: 'text', value: date },
      [this.headers[3]]: {
        type: 'badge',
        shape: 'round',
        kind: status.kind,
        label: status.label,
      },
    };

    if (
      this.selectedAccountHasAuthor &&
      this.selectedAccountHasAssignmentManager
    ) {
      row[this.headers[4]] = {
        type: 'tooltip',
        value: [],
        label: assignmentCount,
      };
      row[this.headers[5]] = {
        type: 'badge',
        shape: 'rectangle',
        kind: 'gray',
        label: simulationType,
      };
    } else {
      row[this.headers[4]] = {
        type: 'badge',
        shape: 'rectangle',
        kind: 'gray',
        label: simulationType,
      };
    }

    return { ...row, options } as unknown as TableRow;
  }

  onTableRowAction(event: {
    action: KebabMenuItem;
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    if (!event.action.text) return;

    // Extract UUID from combined ID (format: 'uuid_moduleUuid' or just 'uuid')
    const simulationUuid = this.extractSimulationUuid(event.rowId, event.rowData);
    const moduleUuid = (event.rowData as any).moduleUuid || '';

    switch (event.action.text.toLowerCase()) {
      case 'delete':
        this.deleteSimulation.emit({
          id: simulationUuid,
          rowData: event.rowData,
          moduleUuid,
        });
        break;
      case 'duplicate':
        this.duplicateSimulation.emit({
          id: simulationUuid,
          name: (event.rowData['Name'] as TableRowSimulationNameObject).value,
          moduleUuid,
        });
        break;
      case 'copy uuid':
        // Copy the original UUID, not the combined ID
        this.clipboard.copy(simulationUuid);
        break;
      default:
        break;
    }
  }

  onTableRowClick(event: {
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    // Extract UUID from combined ID and moduleUuid from rowData
    const simulationUuid = this.extractSimulationUuid(event.rowId, event.rowData);
    const moduleUuid = (event.rowData as any).moduleUuid;
    this.handleEdit(simulationUuid, moduleUuid);
  }

  private handleEdit(rowId: string | number, moduleUuid?: string) {
    // Find simulation using both uuid and moduleUuid for accurate matching
    const simulation = this.simulations.find((sim) =>
      sim.uuid === String(rowId) &&
      (!moduleUuid || sim.moduleUuid === moduleUuid)
    );

    // Find tableRow by matching simulationUuid or by id (for backward compatibility)
    const tableRow = this.tableData.find((row) => {
      const rowSimulationUuid = (row as any).simulationUuid;
      return rowSimulationUuid === String(rowId) || row.id === rowId;
    });
    const simulationTypeBadge = tableRow?.['Simulation type'] as BadgeCell;

    const status = simulation?.status || '';
    const callType = simulationTypeBadge?.label || null;
    const finalModuleUuid = simulation?.moduleUuid || moduleUuid || '';

    const queryParams: any = {};
    if (finalModuleUuid) {
      queryParams.moduleUuid = finalModuleUuid;
    }

    if (status) {
      queryParams.status = status.toLocaleLowerCase();
    }

    this.router.navigate(['simulations', rowId], {
      relativeTo: this.route,
      queryParams,
      state: { callType },
    });
  }

  onTooltipDataRequested = ({ row }: { row: { id: string | number } }) => {
    // Extract UUID from combined ID
    const simulationId = this.extractSimulationUuid(row.id, row as TableRow);
    const moduleUuid = (row as any).moduleUuid || '';

    this.isTooltipLoading = true;

    this.assignmentsService
      .findAssignmentsForSimulation(simulationId, moduleUuid)
      .pipe(
        tap((res) =>
          console.log(
            `Fetched all assignments for simulation ${simulationId}`,
            res
          )
        ),
        finalize(() => {
          this.isTooltipLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: (assignments: IAssignment[]) => {
          const assignmentNames = assignments.map((a) => a.name || 'Untitled');
          this.qaiTableRef?.setTooltipData(assignmentNames);
        },
        error: (err: any) => {
          console.error(
            `Failed to fetch assignments for simulation ${simulationId}:`,
            err
          );
          this.qaiTableRef?.setTooltipData(['Error loading assignments']);
        },
      });
  };

  /**
   * Extracts the simulation UUID from a combined ID.
   * Combined ID format: 'uuid_moduleUuid' or just 'uuid' if no moduleUuid
   */
  private extractSimulationUuid(rowId: string | number, rowData: TableRow): string {
    // If simulationUuid is stored separately, use it (most reliable)
    if ((rowData as any).simulationUuid) {
      return String((rowData as any).simulationUuid);
    }

    // Otherwise, extract from combined ID
    const combinedId = String(rowId);
    // Check if it's a combined ID (contains underscore separator)
    if (combinedId.includes('_')) {
      return combinedId.split('_')[0];
    }

    // Fallback: return as-is if not combined
    return combinedId;
  }

  private formatAssignmentCount(assignmentCount: number, status: string): string {
    if (status.toLowerCase() !== 'published') {
      return '';
    }

    if (!assignmentCount || assignmentCount === 0) {
      return 'None';
    }

    return `${assignmentCount} assignment${assignmentCount > 1 ? 's' : ''}`;
  }

  onPageChanged(event: { page: number }): void {
    this.currentPage = event.page;

    this.PaginationControllerService.setSimulationsCurrentPage(this.currentPage);
  }
}
